"use client";

/**
 * image_text renderer — side-by-side image + text. Defaults to image-on-right;
 * setting `layout_variant: 'left'` flips the image to the left. Optional CTA
 * shows beneath the description.
 *
 * Data: section.content {
 *   eyebrow?, title?, description?, imageUrl?, imageAlt?,
 *   ctaText?, ctaLink?, secondaryCtaText?, secondaryCtaLink?
 * }
 */

import type { VisualCmsSection } from "@/types/visual-cms";
import { SECTION_CONTAINER, sectionShellStyle } from "./section-shell";

function readString(c: unknown, key: string): string {
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const v = (c as Record<string, unknown>)[key];
    return typeof v === "string" ? v : "";
  }
  return "";
}

export function ImageTextSection({ section }: { section: VisualCmsSection }) {
  const shell = sectionShellStyle(section);
  const content = (section.content as Record<string, unknown>) ?? {};
  const eyebrow = readString(content, "eyebrow");
  const title = readString(content, "title");
  const description = readString(content, "description");
  const imageUrl = readString(content, "imageUrl");
  const imageAlt = readString(content, "imageAlt");
  const ctaText = readString(content, "ctaText");
  const ctaLink = readString(content, "ctaLink");
  const imageOnLeft = (section.layout_variant ?? "").toLowerCase() === "left";

  return (
    <section className={shell.className} style={shell.style}>
      <div className={SECTION_CONTAINER}>
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className={imageOnLeft ? "lg:order-2" : ""}>
            {/* {eyebrow && (
              <p className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-green-500 mb-3">
                {eyebrow}
              </p>
            )} */}
            {title && (
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-current mb-4">
                {title}
              </h2>
            )}
            {description && (
              <p className="text-base sm:text-lg text-current/75 leading-relaxed whitespace-pre-wrap">
                {description}
              </p>
            )}
            {ctaText && (
              <div className="mt-6">
                <a
                  href={ctaLink || "#"}
                  className="inline-flex items-center justify-center rounded-md bg-green-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-green-700"
                >
                  {ctaText}
                </a>
              </div>
            )}
          </div>

          <div className={imageOnLeft ? "lg:order-1" : ""}>
            {imageUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={imageUrl}
                alt={imageAlt || title || ""}
                className="w-full h-auto rounded-xl shadow-md object-cover aspect-[4/3]"
              />
            ) : (
              <div className="w-full aspect-[4/3] rounded-xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-sm text-gray-400">
                No image
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
